import { IBoaOnTableFilters, IUserSettings } from '@/interfaces/powerMarkets/settings/IUserSettings.ts';
import { DEFAULT_LEADERBOARD_SETTINGS } from '@/interfaces/powerMarkets/settings/pages/ILeaderboardSettings.ts';
import { DEFAULT_SITE_SETTINGS } from '@/constants/powerMarkets/defaultSiteSettings.ts';
import { DEFAULT_EUROPEAN_INDEX_SETTINGS, DEFAULT_GB_INDEX_SETTINGS } from '@/interfaces/benchmarks/index/IIndexLegacySettings.ts';
import { BidOnTableType } from '@/interfaces/powerMarkets/balancing/IBoaItem.ts';

const DEFAULT_DOUBLE_VALUES = {
    generationCapacity: { min: null, max: null },
    demandCapacity: { min: null, max: null },
    mzt: { min: null, max: null },
    ndb: { min: null, max: null },
    ndo: { min: null, max: null },
    ndz: { min: null, max: null },
    mnzt: { min: null, max: null },
    sil: { min: null, max: null },
    mdv: { min: null, max: null },
    mdp: { min: null, max: null },
};

function getDefaultBoaOnTableFilters (isOffer: boolean): IBoaOnTableFilters {
    return {
        filters: {
            fuelType: {
                filteredTypes: [],
            },
            bidType: {
                filteredTypes: isOffer ? [BidOnTableType.TurnOn, BidOnTableType.TurnUp] : [BidOnTableType.TurnOff, BidOnTableType.TurnDown],
            },
            plantSearch: [],
            mainPrice: [],
            volume: [],
            volumeComparison: [],
            terreAccepted: [],
            cost: [],
            mnzt: [],
            mzt: [],
            ndb: [],
            ndo: [],
            ndz: [],
            sel: [],
        },
        sorting: {
            metric: 'mainPrice',
            order: 'asc',
        },
    };
}

export const DEFAULT_BOA_BID_ON_TABLE_FILTERS: IBoaOnTableFilters = getDefaultBoaOnTableFilters(false);
export const DEFAULT_BOA_OFFER_ON_TABLE_FILTERS: IBoaOnTableFilters = getDefaultBoaOnTableFilters(true);

export const DEFAULT_USER_SETTINGS: IUserSettings = {
    otherSettings: {
        data: DEFAULT_SITE_SETTINGS,
        id: '',
    },
    pageData: {
        bmDates: {
            id: '',
            data: {},
        },
        bmLeaderboard: {
            id: '',
            data: {
                userOptions: DEFAULT_LEADERBOARD_SETTINGS.userOptions,
            },
        },
        plantIndex: {
            id: '',
            data: DEFAULT_GB_INDEX_SETTINGS,
        },
        europeIndex: {
            id: '',
            data: DEFAULT_EUROPEAN_INDEX_SETTINGS,
        },
        boaDashboardMap: {
            id: '',
            data: {
                interconnectorOption: 'Outturn / Forecast',
                showInterconnectors: true,
                showConstraintRegions: false,
                showNetworkBoundaries: false,
                networkBoundaryOption: null,
                showWesternHvdcLine: true,
                showZones: true,
                zoneOption: null,
                flaggedOnly: false,
                colourByBod: true,
            },
        },
        boa: {
            id: '',
            data: {
                currentView: 'tableMap',
                analyticsOptions: {
                    edit: true,
                    addition: true,
                    nonBm: true,
                    minSel: true,
                    key: 0,
                    noticeDeviateFilter: -1,
                },
                chartBidOfferToShow: 'offers',
                chartBidOnOffUpDown: ['onOff', 'upDown'],
                chartNoticeToDeviateFilter: '',
                chartMaxPrice: 8000,
                chartHideUnwind: false,
                chartHideWind: false,
                bidOnTableFilters: DEFAULT_BOA_BID_ON_TABLE_FILTERS,
                offerOnTableFilters: DEFAULT_BOA_OFFER_ON_TABLE_FILTERS,
                boaFilterOptions: {
                    metrics: {
                        id: 'metricsParent',
                        title: 'Metrics',
                        selected: true,
                        childOptions: [
                            { id: 'showSystemPrice', title: 'System Price', selected: true },
                            { id: 'showIMBALNGC', title: 'IMBALNGC', selected: true },
                            { id: 'showLCPNIVForecast', title: 'LCP NIV Forecast', selected: true },
                            { id: 'showCurrentSystemImbalance', title: 'Current System Imbalance', selected: true },
                            { id: 'showAvailableTurnUp', title: 'Available Turn Up', selected: true },
                            { id: 'showAvailableTurnDown', title: 'Available Turn Down', selected: true },
                            { id: 'showIvcNegative', title: 'IVC Negative', selected: false },
                            { id: 'showIvcPositive', title: 'IVC Positive', selected: false },
                            { id: 'showApxPrice', title: 'APX Price', selected: false },
                        ],
                    },
                    breakdown: {
                        id: 'breakdownParent',
                        title: 'Breakdown',
                        selected: false,
                        childOptions: [
                            { id: 'showWindForecast', title: 'Wind Forecast', selected: false },
                            { id: 'showDemandForecast', title: 'Demand Forecast', selected: false },
                            { id: 'showINDGEN', title: 'INDGEN', selected: false },
                            { id: 'showPlantUnavailability', title: 'Plant Unavailability', selected: false },
                            { id: 'showOtherAdjustments', title: 'Other Adjustments', selected: false },
                            { id: 'showNivForecast', title: 'NIV Forecast', selected: false },
                        ],
                    },
                    bsad: {
                        id: 'bsadParent',
                        title: 'BSAD',
                        selected: false,
                        childOptions: [
                            { id: 'showTotalBSAD', title: 'Total BSAD', selected: false },
                            { id: 'showEnergy', title: 'Energy', selected: false },
                            { id: 'showSystem', title: 'System', selected: false },
                            { id: 'showNonBMFR', title: 'Non-BM FR', selected: false },
                            { id: 'showNonBMSTOR', title: 'Non-BM STOR', selected: false },
                            { id: 'showOther', title: 'Other BSAD', selected: false },
                            { id: 'showRdpNegative', title: 'RDP Negative', selected: false },
                            { id: 'showNonBMLCM', title: 'Non-BM LCM', selected: false },
                            { id: 'showNonBMPQR', title: 'Non-BM PQR', selected: false },
                            { id: 'showNonBMNQR', title: 'Non-BM NQR', selected: false },
                        ],
                    },
                    flagging: {
                        id: 'flaggingParent',
                        title: 'Flagging',
                        selected: false,
                        childOptions: [
                            { id: 'showSOFlagged', title: 'SO Flagged', selected: false },
                            { id: 'showSTORFlagged', title: 'STOR Flagged', selected: false },
                            { id: 'showCADLFlagged', title: 'CADL Flagged', selected: false },
                            { id: 'showUnflagged', title: 'Unflagged', selected: false },
                        ],
                    },
                    windCurtailment: {
                        id: 'windCurtailmentParent',
                        title: 'Wind Curtailment',
                        selected: false,
                        childOptions: [
                            { id: 'showWindCurtailmentTotal', title: 'Total Wind Curtailment', selected: false },
                            { id: 'showWindCurtailmentFlagged', title: 'Flagged Wind Curtailment', selected: false },
                            { id: 'showWindCurtailmentSOFlagged', title: 'SO Flagged Wind Curtailment', selected: false },
                            { id: 'showWindCurtailmentUnflagged', title: 'Unflagged Wind Curtailment', selected: false },
                        ],
                    },
                },
            },
        },
        historicQueries: {
            id: '',
            data: [],
        },
        map: {
            id: '',
            data: {
                interconnectorOption: 'Outturn / Forecast',
                plantFilters: {
                    doubleValues: DEFAULT_DOUBLE_VALUES,
                    fuel: [],
                    extraFuel: [],
                    owner: [],
                    status: [],
                    type: [],
                },
                plantOption: 'None',
                showInterconnectors: true,
                showPlants: false,
                showZones: true,
                zoneOption: null,
                showConstraintRegions: false,
                showNetworkBoundaries: false,
                networkBoundaryOption: null,
                showWesternHvdcLine: true,
                colourByBod: false,
                flaggedOnly: false,
            },
        },
        mapLive: {
            id: '',
            data: {
                showZoneDataOnMap: true,
                selectedActionIds: [],
                selectedTimePeriod: 'live',
                selectedBoard: null,
            },
        },
        pageDates: {
            id: '',
            data: {},
        },
        remit: {
            id: '',
            data: {
                userOptions: {
                    remitType: 'Remit',
                    additionalType: 'All',
                    tableViewSelected: 'Summarised',
                },
            },
        },
        subscriptions: {
            id: '',
            data: {
                allSubscriptions: [],
                subscriptionsInOrder: [],
            },
        },
        plantActivity: {
            id: '',
            data: {
                firstColumnOption: 'siteWide',
            },
        },
    },
    activeTab: null,
    bingoActiveTab: null,
    bingoWithOrder: { objectsToStore: {}, order: { id: '', data: [] } },
    dashboardsWithOrder: { objectsToStore: {}, order: { id: '', data: [] } },
    indexGroupActiveTab: null,
    indexGroupsWithOrder: { objectsToStore: {}, order: { id: '', data: [] } },
    leaderboardActiveTab: null,
    leaderboardsWithOrder: { objectsToStore: {}, order: { id: '', data: [] } },
    isValid: false,
    subscriptionsWithOrder: { objectsToStore: {}, order: { id: '', data: [] } },
    tableFilters: {},
};
